// ── SmartTraQ i18n — Internationalization System ──
// React context + hook for translations, RTL support, language persistence
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

// ── Supported Languages ──
export const LANGUAGES = [
  { id: "en", name: "English", native: "English", dir: "ltr", flag: "🇬🇧" },
  { id: "ar", name: "Arabic",  native: "العربية", dir: "rtl", flag: "🇦🇪" },
];

// ── English Translations (default) ──
const en = {
  // ── Navigation ──
  "nav.home": "Home",
  "nav.orders": "Orders",
  "nav.shipments": "Shipments",
  "nav.finance": "Finance",
  "nav.tradeDocs": "Trade Docs",
  "nav.tasks": "Tasks",
  "nav.calendar": "Calendar",
  "nav.activity": "Activity",
  "nav.customers": "Customers",
  "nav.vendors": "Vendors",
  "nav.rfqs": "RFQs",
  "nav.products": "Inventory",
  "nav.team": "Team",
  "nav.settings": "Settings",

  // ── Common Actions ──
  "action.save": "Save",
  "action.cancel": "Cancel",
  "action.delete": "Delete",
  "action.archive": "Archive",
  "action.edit": "Edit",
  "action.view": "View",
  "action.create": "Create",
  "action.close": "Close",
  "action.search": "Search",
  "action.filter": "Filter",
  "action.export": "Export",
  "action.import": "Import",
  "action.send": "Send",
  "action.print": "Print",
  "action.copy": "Copy",
  "action.duplicate": "Duplicate",
  "action.restore": "Restore",
  "action.confirm": "Confirm",
  "action.back": "Back",
  "action.next": "Next",
  "action.done": "Done",
  "action.clear": "Clear",
  "action.reset": "Reset",
  "action.apply": "Apply",
  "action.add": "Add",
  "action.remove": "Remove",
  "action.refresh": "Refresh",
  "action.signOut": "Sign Out",
  "action.invite": "Invite",

  // ── Common Labels ──
  "label.name": "Name",
  "label.email": "Email",
  "label.phone": "Phone",
  "label.address": "Address",
  "label.status": "Status",
  "label.date": "Date",
  "label.amount": "Amount",
  "label.total": "Total",
  "label.subtotal": "Subtotal",
  "label.notes": "Notes",
  "label.description": "Description",
  "label.priority": "Priority",
  "label.assignee": "Assignee",
  "label.dueDate": "Due Date",
  "label.type": "Type",
  "label.quantity": "Quantity",
  "label.price": "Price",
  "label.currency": "Currency",
  "label.company": "Company",
  "label.contact": "Contact",
  "label.country": "Country",
  "label.all": "All",
  "label.active": "Active",
  "label.archived": "Archived",
  "label.selected": "selected",
  "label.total_lower": "total",
  "label.more": "More",
  "label.loading": "Loading...",
  "label.noResults": "No results",
  "label.yes": "Yes",
  "label.no": "No",

  // ── Dashboard ──
  "dash.greeting.morning": "Good morning",
  "dash.greeting.afternoon": "Good afternoon",
  "dash.greeting.evening": "Good evening",
  "dash.subtitle": "Here's your trade operations overview",
  "dash.activeOrders": "Active Orders",
  "dash.inTransit": "In Transit",
  "dash.customers": "Customers",
  "dash.monthlyVolume": "Monthly Volume",
  "dash.recentOrders": "Recent Orders",
  "dash.dailyBriefing": "Daily Briefing",
  "dash.quickActions": "Quick Actions",
  "dash.newOrder": "New Order",
  "dash.newShipment": "New Shipment",
  "dash.newCustomer": "New Customer",
  "dash.viewAll": "View All",
  "dash.noOrders": "No orders yet",
  "dash.pipeline": "Pipeline",
  "dash.revenue": "Revenue",
  "dash.pendingTasks": "Pending Tasks",

  // ── Orders ──
  "ord.title": "Orders",
  "ord.newOrder": "New Order",
  "ord.purchase": "Purchase",
  "ord.sales": "Sales",
  "ord.forwarding": "Forwarding",
  "ord.board": "Board",
  "ord.list": "List",
  "ord.searchPlaceholder": "Search orders...",
  "ord.noOrders": "No orders yet",
  "ord.noOrdersSub": "Create your first order to start tracking your trade operations.",
  "ord.addFirst": "Add First Order",
  "ord.stage": "Stage",
  "ord.customer": "Customer",
  "ord.vendor": "Vendor",
  "ord.value": "Value",
  "ord.eta": "ETA",
  "ord.actions": "Actions",

  // ── Shipments ──
  "shp.title": "Shipments",
  "shp.newShipment": "New Shipment",
  "shp.inTransit": "in transit",
  "shp.searchPlaceholder": "Search shipments...",
  "shp.noShipments": "No shipments yet",
  "shp.noShipmentsSub": "Add a shipment to track carriers, tracking numbers, and delivery status.",
  "shp.addFirst": "Add First Shipment",
  "shp.carrier": "Carrier",
  "shp.route": "Route",
  "shp.freight": "Freight",
  "shp.track": "Track",
  "shp.processing": "Processing",
  "shp.delivered": "Delivered",

  // ── Customers ──
  "cust.title": "Customers",
  "cust.newCustomer": "New Customer",
  "cust.searchPlaceholder": "Search customers...",
  "cust.noCustomers": "No customers yet",
  "cust.noCustomersSub": "Add your first customer to track their orders, pipeline stage, and contact details.",
  "cust.addFirst": "Add First Customer",
  "cust.pipeline": "Pipeline",
  "cust.orders": "Orders",
  "cust.value": "Value",

  // ── Vendors ──
  "vend.title": "Vendors",
  "vend.newVendor": "New Vendor",
  "vend.searchPlaceholder": "Search vendors...",
  "vend.noVendors": "No vendors yet",
  "vend.noVendorsSub": "Add vendors to manage procurement, send RFQs, and track performance ratings.",
  "vend.addFirst": "Add First Vendor",
  "vend.rating": "Rating",
  "vend.specialties": "Specialties",

  // ── Tasks ──
  "task.title": "Tasks",
  "task.newTask": "New Task",
  "task.todo": "To Do",
  "task.inProgress": "In Progress",
  "task.review": "Review",
  "task.done": "Done",
  "task.myTasks": "My Tasks",
  "task.allTasks": "All Tasks",
  "task.noTasks": "No tasks yet",
  "task.kanban": "Kanban",

  // ── Finance ──
  "fin.title": "Finance",
  "fin.invoices": "Invoices",
  "fin.newInvoice": "New Invoice",
  "fin.aging": "Aging Report",
  "fin.receivable": "Receivable",
  "fin.payable": "Payable",
  "fin.paid": "Paid",
  "fin.overdue": "Overdue",
  "fin.draft": "Draft",
  "fin.sent": "Sent",
  "fin.partial": "Partial",

  // ── Products ──
  "prod.title": "Products",
  "prod.newProduct": "New Product",
  "prod.catalog": "Catalog",
  "prod.stock": "Stock",
  "prod.movements": "Movements",
  "prod.onHand": "On Hand",
  "prod.committed": "Committed",
  "prod.available": "Available",
  "prod.lowStock": "Low Stock",
  "prod.reorderLevel": "Reorder Level",

  // ── Trade Docs ──
  "tdoc.title": "Trade Documents",
  "tdoc.ci": "Commercial Invoice",
  "tdoc.pl": "Packing List",
  "tdoc.pi": "Proforma Invoice",
  "tdoc.po": "Purchase Order",

  // ── Team ──
  "team.title": "Team Members",
  "team.invite": "Invite a Team Member",
  "team.sendInvite": "Send Invite",
  "team.activity": "Team Activity",
  "team.assignTask": "Assign Task to Team",

  // ── Settings ──
  "sett.title": "Settings",
  "sett.subtitle": "Manage your workspace",
  "sett.account": "Account",
  "sett.security": "Security",
  "sett.notifications": "Notifications",
  "sett.appearance": "Appearance",
  "sett.organization": "Organization",
  "sett.documents": "Documents",
  "sett.finance": "Finance",
  "sett.templates": "Order Templates",
  "sett.inviteTeam": "Invite Team",
  "sett.rolesAccess": "Roles & Access",
  "sett.dataReset": "Data & Reset",
  "sett.language": "Language",
  "sett.languageSub": "Choose your preferred language. Arabic enables right-to-left layout.",
  "sett.theme": "Theme",
  "sett.accent": "Accent Color",

  // ── Calendar ──
  "cal.title": "Calendar",
  "cal.today": "Today",
  "cal.month": "Month",
  "cal.week": "Week",

  // ── Activity Log ──
  "log.title": "Activity Log",
  "log.noActivity": "No activity yet",

  // ── RFQs ──
  "rfq.title": "RFQs",
  "rfq.newRfq": "New RFQ",
  "rfq.pending": "Pending",
  "rfq.responded": "Responded",
  "rfq.awarded": "Awarded",

  // ── Empty States ──
  "empty.noData": "No data",
  "empty.getStarted": "Get started by creating your first item",

  // ── Misc / App ──
  "app.name": "SmartTraQ",
  "app.tagline": "Trade & Logistics",
  "app.search": "Search anything...",
  "app.aiAssistant": "AI Assistant",
  "app.notifications": "Notifications",
  "app.noNotifications": "No notifications",
  "app.help": "Help",
  "app.manual": "Manual",
  "app.appGuide": "App Guide",
  "app.install": "Install App",
  "app.installSub": "Add SmartTraQ to your home screen for a native app experience",
  "app.installBtn": "Install",
  "app.installDismiss": "Not now",

  // ── PWA ──
  "pwa.install": "Install SmartTraQ",
  "pwa.installSub": "Get the full native app experience — works offline, launches from your home screen.",
  "pwa.installBtn": "Install App",
  "pwa.dismiss": "Maybe Later",
  "pwa.offline": "You're offline — some features may be limited",
  "pwa.updated": "App updated! Refresh for the latest version.",
};

// ── Arabic Translations ──
const ar = {
  // ── Navigation ──
  "nav.home": "الرئيسية",
  "nav.orders": "الطلبات",
  "nav.shipments": "الشحنات",
  "nav.finance": "المالية",
  "nav.tradeDocs": "مستندات التجارة",
  "nav.tasks": "المهام",
  "nav.calendar": "التقويم",
  "nav.activity": "النشاط",
  "nav.customers": "العملاء",
  "nav.vendors": "الموردون",
  "nav.rfqs": "عروض الأسعار",
  "nav.products": "المخزون",
  "nav.team": "الفريق",
  "nav.settings": "الإعدادات",

  // ── Common Actions ──
  "action.save": "حفظ",
  "action.cancel": "إلغاء",
  "action.delete": "حذف",
  "action.archive": "أرشفة",
  "action.edit": "تعديل",
  "action.view": "عرض",
  "action.create": "إنشاء",
  "action.close": "إغلاق",
  "action.search": "بحث",
  "action.filter": "تصفية",
  "action.export": "تصدير",
  "action.import": "استيراد",
  "action.send": "إرسال",
  "action.print": "طباعة",
  "action.copy": "نسخ",
  "action.duplicate": "نسخ مكرر",
  "action.restore": "استعادة",
  "action.confirm": "تأكيد",
  "action.back": "رجوع",
  "action.next": "التالي",
  "action.done": "تم",
  "action.clear": "مسح",
  "action.reset": "إعادة تعيين",
  "action.apply": "تطبيق",
  "action.add": "إضافة",
  "action.remove": "إزالة",
  "action.refresh": "تحديث",
  "action.signOut": "تسجيل الخروج",
  "action.invite": "دعوة",

  // ── Common Labels ──
  "label.name": "الاسم",
  "label.email": "البريد الإلكتروني",
  "label.phone": "الهاتف",
  "label.address": "العنوان",
  "label.status": "الحالة",
  "label.date": "التاريخ",
  "label.amount": "المبلغ",
  "label.total": "الإجمالي",
  "label.subtotal": "المجموع الفرعي",
  "label.notes": "ملاحظات",
  "label.description": "الوصف",
  "label.priority": "الأولوية",
  "label.assignee": "المكلف",
  "label.dueDate": "تاريخ الاستحقاق",
  "label.type": "النوع",
  "label.quantity": "الكمية",
  "label.price": "السعر",
  "label.currency": "العملة",
  "label.company": "الشركة",
  "label.contact": "جهة الاتصال",
  "label.country": "الدولة",
  "label.all": "الكل",
  "label.active": "نشط",
  "label.archived": "مؤرشف",
  "label.selected": "محدد",
  "label.total_lower": "إجمالي",
  "label.more": "المزيد",
  "label.loading": "جاري التحميل...",
  "label.noResults": "لا توجد نتائج",
  "label.yes": "نعم",
  "label.no": "لا",

  // ── Dashboard ──
  "dash.greeting.morning": "صباح الخير",
  "dash.greeting.afternoon": "مساء الخير",
  "dash.greeting.evening": "مساء الخير",
  "dash.subtitle": "نظرة عامة على عمليات التجارة",
  "dash.activeOrders": "الطلبات النشطة",
  "dash.inTransit": "في الطريق",
  "dash.customers": "العملاء",
  "dash.monthlyVolume": "الحجم الشهري",
  "dash.recentOrders": "الطلبات الأخيرة",
  "dash.dailyBriefing": "الإحاطة اليومية",
  "dash.quickActions": "إجراءات سريعة",
  "dash.newOrder": "طلب جديد",
  "dash.newShipment": "شحنة جديدة",
  "dash.newCustomer": "عميل جديد",
  "dash.viewAll": "عرض الكل",
  "dash.noOrders": "لا توجد طلبات بعد",
  "dash.pipeline": "خط الأنابيب",
  "dash.revenue": "الإيرادات",
  "dash.pendingTasks": "المهام المعلقة",

  // ── Orders ──
  "ord.title": "الطلبات",
  "ord.newOrder": "طلب جديد",
  "ord.purchase": "شراء",
  "ord.sales": "مبيعات",
  "ord.forwarding": "شحن",
  "ord.board": "لوحة",
  "ord.list": "قائمة",
  "ord.searchPlaceholder": "البحث في الطلبات...",
  "ord.noOrders": "لا توجد طلبات بعد",
  "ord.noOrdersSub": "أنشئ أول طلب لبدء تتبع عمليات التجارة.",
  "ord.addFirst": "إضافة أول طلب",
  "ord.stage": "المرحلة",
  "ord.customer": "العميل",
  "ord.vendor": "المورد",
  "ord.value": "القيمة",
  "ord.eta": "وقت الوصول المتوقع",
  "ord.actions": "الإجراءات",

  // ── Shipments ──
  "shp.title": "الشحنات",
  "shp.newShipment": "شحنة جديدة",
  "shp.inTransit": "في الطريق",
  "shp.searchPlaceholder": "البحث في الشحنات...",
  "shp.noShipments": "لا توجد شحنات بعد",
  "shp.noShipmentsSub": "أضف شحنة لتتبع الناقلين وأرقام التتبع وحالة التسليم.",
  "shp.addFirst": "إضافة أول شحنة",
  "shp.carrier": "الناقل",
  "shp.route": "المسار",
  "shp.freight": "الشحن",
  "shp.track": "تتبع",
  "shp.processing": "قيد المعالجة",
  "shp.delivered": "تم التسليم",

  // ── Customers ──
  "cust.title": "العملاء",
  "cust.newCustomer": "عميل جديد",
  "cust.searchPlaceholder": "البحث في العملاء...",
  "cust.noCustomers": "لا يوجد عملاء بعد",
  "cust.noCustomersSub": "أضف أول عميل لتتبع طلباتهم ومرحلة خط الأنابيب وتفاصيل الاتصال.",
  "cust.addFirst": "إضافة أول عميل",
  "cust.pipeline": "خط الأنابيب",
  "cust.orders": "الطلبات",
  "cust.value": "القيمة",

  // ── Vendors ──
  "vend.title": "الموردون",
  "vend.newVendor": "مورد جديد",
  "vend.searchPlaceholder": "البحث في الموردين...",
  "vend.noVendors": "لا يوجد موردون بعد",
  "vend.noVendorsSub": "أضف موردين لإدارة المشتريات وإرسال عروض الأسعار وتتبع تقييمات الأداء.",
  "vend.addFirst": "إضافة أول مورد",
  "vend.rating": "التقييم",
  "vend.specialties": "التخصصات",

  // ── Tasks ──
  "task.title": "المهام",
  "task.newTask": "مهمة جديدة",
  "task.todo": "للتنفيذ",
  "task.inProgress": "قيد التنفيذ",
  "task.review": "مراجعة",
  "task.done": "مكتملة",
  "task.myTasks": "مهامي",
  "task.allTasks": "كل المهام",
  "task.noTasks": "لا توجد مهام بعد",
  "task.kanban": "كانبان",

  // ── Finance ──
  "fin.title": "المالية",
  "fin.invoices": "الفواتير",
  "fin.newInvoice": "فاتورة جديدة",
  "fin.aging": "تقرير الأعمار",
  "fin.receivable": "مستحقات",
  "fin.payable": "مدفوعات",
  "fin.paid": "مدفوع",
  "fin.overdue": "متأخر",
  "fin.draft": "مسودة",
  "fin.sent": "مُرسل",
  "fin.partial": "جزئي",

  // ── Products ──
  "prod.title": "المنتجات",
  "prod.newProduct": "منتج جديد",
  "prod.catalog": "الكتالوج",
  "prod.stock": "المخزون",
  "prod.movements": "الحركات",
  "prod.onHand": "المتوفر",
  "prod.committed": "محجوز",
  "prod.available": "متاح",
  "prod.lowStock": "مخزون منخفض",
  "prod.reorderLevel": "مستوى إعادة الطلب",

  // ── Trade Docs ──
  "tdoc.title": "مستندات التجارة",
  "tdoc.ci": "فاتورة تجارية",
  "tdoc.pl": "قائمة التعبئة",
  "tdoc.pi": "فاتورة أولية",
  "tdoc.po": "أمر شراء",

  // ── Team ──
  "team.title": "أعضاء الفريق",
  "team.invite": "دعوة عضو للفريق",
  "team.sendInvite": "إرسال الدعوة",
  "team.activity": "نشاط الفريق",
  "team.assignTask": "تعيين مهمة للفريق",

  // ── Settings ──
  "sett.title": "الإعدادات",
  "sett.subtitle": "إدارة مساحة العمل",
  "sett.account": "الحساب",
  "sett.security": "الأمان",
  "sett.notifications": "الإشعارات",
  "sett.appearance": "المظهر",
  "sett.organization": "المنظمة",
  "sett.documents": "المستندات",
  "sett.finance": "المالية",
  "sett.templates": "قوالب الطلبات",
  "sett.inviteTeam": "دعوة الفريق",
  "sett.rolesAccess": "الأدوار والصلاحيات",
  "sett.dataReset": "البيانات والإعادة",
  "sett.language": "اللغة",
  "sett.languageSub": "اختر لغتك المفضلة. اللغة العربية تفعّل التخطيط من اليمين إلى اليسار.",
  "sett.theme": "السمة",
  "sett.accent": "لون التمييز",

  // ── Calendar ──
  "cal.title": "التقويم",
  "cal.today": "اليوم",
  "cal.month": "شهر",
  "cal.week": "أسبوع",

  // ── Activity Log ──
  "log.title": "سجل النشاط",
  "log.noActivity": "لا يوجد نشاط بعد",

  // ── RFQs ──
  "rfq.title": "عروض الأسعار",
  "rfq.newRfq": "عرض سعر جديد",
  "rfq.pending": "معلق",
  "rfq.responded": "تمت الاستجابة",
  "rfq.awarded": "تم الترسية",

  // ── Empty States ──
  "empty.noData": "لا توجد بيانات",
  "empty.getStarted": "ابدأ بإنشاء أول عنصر",

  // ── Misc / App ──
  "app.name": "SmartTraQ",
  "app.tagline": "التجارة واللوجستيات",
  "app.search": "ابحث عن أي شيء...",
  "app.aiAssistant": "المساعد الذكي",
  "app.notifications": "الإشعارات",
  "app.noNotifications": "لا توجد إشعارات",
  "app.help": "مساعدة",
  "app.manual": "الدليل",
  "app.appGuide": "دليل التطبيق",
  "app.install": "تثبيت التطبيق",
  "app.installSub": "أضف SmartTraQ إلى شاشتك الرئيسية لتجربة تطبيق أصلية",
  "app.installBtn": "تثبيت",
  "app.installDismiss": "ليس الآن",

  // ── PWA ──
  "pwa.install": "تثبيت SmartTraQ",
  "pwa.installSub": "احصل على تجربة التطبيق الأصلي — يعمل بدون اتصال، يفتح من شاشتك الرئيسية.",
  "pwa.installBtn": "تثبيت التطبيق",
  "pwa.dismiss": "ربما لاحقاً",
  "pwa.offline": "أنت غير متصل — بعض الميزات قد تكون محدودة",
  "pwa.updated": "تم تحديث التطبيق! قم بالتحديث للحصول على أحدث إصدار.",
};

// ── All translations ──
const translations = { en, ar };

// ── Context ──
const I18nContext = createContext(null);

// ── LS Key ──
const LS_LANG_KEY = "stq_lang";

// ── Provider ──
export function I18nProvider({ children, initialLang }) {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_LANG_KEY);
      if (saved && translations[saved]) return saved;
    } catch {}
    return initialLang || "en";
  });

  const setLang = useCallback((newLang) => {
    if (!translations[newLang]) return;
    setLangState(newLang);
    try { localStorage.setItem(LS_LANG_KEY, newLang); } catch {}
    // Update document direction
    document.documentElement.dir = LANGUAGES.find(l => l.id === newLang)?.dir || "ltr";
    document.documentElement.lang = newLang;
  }, []);

  // Set direction on mount
  useEffect(() => {
    const info = LANGUAGES.find(l => l.id === lang);
    document.documentElement.dir = info?.dir || "ltr";
    document.documentElement.lang = lang;
  }, []);

  const t = useCallback((key, params) => {
    const dict = translations[lang] || translations.en;
    let val = dict[key] || translations.en[key] || key;
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        val = val.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
      });
    }
    return val;
  }, [lang]);

  const isRTL = useMemo(() => {
    return LANGUAGES.find(l => l.id === lang)?.dir === "rtl";
  }, [lang]);

  const dir = isRTL ? "rtl" : "ltr";

  const value = useMemo(() => ({ lang, setLang, t, isRTL, dir }), [lang, setLang, t, isRTL, dir]);

  return React.createElement(I18nContext.Provider, { value }, children);
}

// ── Hook ──
export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Fallback for components outside provider
    return {
      lang: "en",
      setLang: () => {},
      t: (key) => translations.en[key] || key,
      isRTL: false,
      dir: "ltr",
    };
  }
  return ctx;
}

// ── Standalone translate (for non-component code) ──
export function translate(key, lang = "en") {
  const dict = translations[lang] || translations.en;
  return dict[key] || translations.en[key] || key;
}

// ── Get saved language ──
export function getSavedLang() {
  try {
    const saved = localStorage.getItem(LS_LANG_KEY);
    if (saved && translations[saved]) return saved;
  } catch {}
  return "en";
}
